import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SellerSearchResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

/** Debounce delay in milliseconds before firing a search request */
const DEBOUNCE_MS = 250;

/** Minimum query length before enabling the search request */
const MIN_QUERY_LENGTH = 2;

type UseGlobalSearchQueryParams = {
  storeSlug: string;
  q: string;
};

/**
 * Debounced global search hook. Mirrors the pattern in
 * `useSellerCustomersQuery.ts` (useQuery + apiJsonClient + queryKeys).
 *
 * - Debounces `q` by ~250ms before enabling the query.
 * - Only enabled when the debounced query is ≥ 2 characters (trimmed).
 * - No refetch on window focus (search results are instant/ephemeral).
 */
export function useGlobalSearchQuery({
  storeSlug,
  q,
}: UseGlobalSearchQueryParams) {
  const [debouncedQ, setDebouncedQ] = useState(q);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [q]);

  const trimmed = debouncedQ.trim();
  const isEnabled =
    storeSlug.length > 0 && trimmed.length >= MIN_QUERY_LENGTH;

  const query = useQuery({
    queryKey: queryKeys.seller.search(storeSlug, trimmed),
    queryFn: () => {
      const params = new URLSearchParams({ q: trimmed });
      return apiJsonClient<SellerSearchResponse>(
        `${endpoints.seller.search}?${params.toString()}`,
      );
    },
    enabled: isEnabled,
    staleTime: 30_000, // 30s — searches are read-only, stale quickly
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  };
}
